<script setup lang="ts">
  import { useTourStore } from '@/stores/tour'
  import { computed, ref } from 'vue'
  import DesButton from '@/components/button/DesButton.vue'
  import DesModalClose from '@/components/modal/DesModalClose.vue'
  import DesSpinner from '@/components/DesSpinner.vue'
  import DesButtonIcon from '@/components/button/DesButtonIcon.vue'

  const tour = useTourStore()
  const videoRef = ref<HTMLVideoElement | null>(null)
  const r = 8
  const x0 = computed(() => tour.gap.x + r)
  const x1 = computed(() => tour.gap.x + tour.gap.w - r)
  const y0 = computed(() => tour.gap.y)
  const y1 = computed(() => tour.gap.y + tour.gap.h)

  const top = computed(() => {
    switch (tour.direction) {
      case 'top-left':
      case 'top-right':
      case 'top-center':
        return `${tour.gap.y + tour.gap.h}px`
      case 'bottom-left':
      case 'bottom-right':
      case 'bottom-center':
        return `${tour.gap.y}px`
      default:
        return `${tour.gap.y + tour.gap.h / 2}px`
    }
  })

  const popupWidth = 384

  const left = computed(() => {
    const { x, w } = tour.gap
    let value

    switch (tour.direction) {
      case 'top-left':
      case 'bottom-left':
        value = x - 20
        break
      case 'top-right':
      case 'bottom-right':
        value = x + w + 20 - popupWidth
        break
      case 'top-center':
      case 'bottom-center':
        value = x + w / 2 - popupWidth / 2
        break
      case 'right':
        value = x - popupWidth
        break
      default:
        value = x + w
    }

    const max = window.innerWidth - popupWidth - 8

    return `${Math.max(8, Math.min(value, max))}px`
  })

  const transform = computed(() => {
    switch (tour.direction) {
      case 'bottom-left':
      case 'bottom-center':
      case 'bottom-right':
        return 'translateY(-100%)'
      case 'left':
      case 'right':
        return 'translateY(-50%)'
      default:
        return 'none'
    }
  })

  const arrowTop = computed(() => {
    switch (tour.direction) {
      case 'right':
      case 'left':
        return `${tour.gap.y + tour.gap.h / 2 - 8}px`
      case 'bottom-left':
      case 'bottom-right':
      case 'bottom-center':
        return `${tour.gap.y - 28}px`
      default:
        return `${tour.gap.y + tour.gap.h + 12}px`
    }
  })

  const arrowLeft = computed(() => {
    switch (tour.direction) {
      case 'left':
        return `${tour.gap.x + tour.gap.w + 10.5}px`
      case 'right':
        return `${tour.gap.x - 31.5}px`
      default:
        return `${tour.gap.x + tour.gap.w / 2 - 10.5}px`
    }
  })

  const filteredList = computed(() => {
    return tour.list.filter((item) => item.group === tour.currentGroupName)
  })

  function toggleFullscreen() {
    if (videoRef.value) {
      if (videoRef.value.requestFullscreen) {
        videoRef.value.requestFullscreen()
      }
    }
  }
</script>

<template>
  <Transition enter-from-class="opacity-0 pointer-events-none" leave-to-class="opacity-0 pointer-events-none">
    <div v-if="tour.visible" class="absolute transition-all z-50 inset-0">
      <!-- backdrop with gap -->
      <svg :viewBox="`0 0 ${tour.backdrop.width} ${tour.backdrop.height}`" class="absolute inset-0 opacity-50">
        <path
          fill-rule="evenodd"
          class="transition-all fill-black-10"
          :d="`
            M0,0 0,${tour.backdrop.height} ${tour.backdrop.width},${tour.backdrop.height} ${tour.backdrop.width},0
            Z
            M${x0},${tour.gap.y}
            H${x1}
            A${r},${r} 0 0 1 ${x1 + r},${tour.gap.y + r}
            V${y1 - r}
            A${r},${r} 0 0 1 ${x1},${y1}
            H${x0}
            A${r},${r} 0 0 1 ${x0 - r},${y1 - r}
            V${y0 + r}
            A${r},${r} 0 0 1 ${x0},${tour.gap.y}
            Z`"
        />
      </svg>

      <!-- popup with information -->
      <div
        class="absolute w-96 transition-all"
        :style="{ top, left, transform }"
        :class="{
          'px-6': tour.direction === 'left' || tour.direction === 'right',
          'py-6': tour.direction !== 'left' && tour.direction !== 'right',
        }"
      >
        <div class="bg-white rounded-xl shadow-xl p-6 relative">
          <DesModalClose @click="tour.hide" class="absolute!" />
          <div class="text-xs text-black-60 mb-2">{{ tour.current + 1 }} of {{ filteredList.length }}</div>
          <div class="font-semibold mb-1">{{ tour.item?.title }}</div>
          <div class="text-sm">{{ tour.item?.content }}</div>
          <div v-if="tour.item?.video || tour.item?.image" class="p-2 bg-white-gray rounded-sm mt-6 relative min-h-48">
            <div v-if="tour.item?.video">
              <DesSpinner />
              <video autoplay loop muted :key="tour.item?.name" class="w-full relative" ref="videoRef" preload="auto">
                <source :src="tour.item?.video" type="video/mp4" />
              </video>
              <DesButtonIcon
                @click.stop="toggleFullscreen"
                icon="fa-light fa-arrows-maximize"
                class="absolute top-2 right-2"
              />
            </div>
            <div v-if="tour.item?.image">
              <img class="w-full relative" :src="tour.item.image" type="video/mp4" />
            </div>
          </div>

          <div class="flex justify-between mt-6">
            <DesButton @click="tour.prev" v-if="tour.current" type="gray">
              <des-icon icon="fa-light fa-arrow-left" class="w-icon h-icon" />
              Prev
            </DesButton>
            <div v-else></div>
            <DesButton @click="tour.next" v-if="tour.current + 1 < filteredList.length">Next</DesButton>
            <DesButton @click="tour.hide" v-else>Done</DesButton>
          </div>
        </div>
      </div>

      <!-- arrow for popup -->
      <svg
        width="21"
        height="16"
        class="transition-all fill-white absolute"
        :class="{
          'rotate-90': tour.direction === 'right',
          '-rotate-90': tour.direction === 'left',
          'rotate-180':
            tour.direction === 'bottom-left' || tour.direction === 'bottom-center' || tour.direction === 'bottom-right',
        }"
        :style="{ top: arrowTop, left: arrowLeft }"
      >
        <path d="M8.92 1.56a2 2 0 0 1 3.16 0l8.07 10.47a2 2 0 0 1-1.59 3.22H2.44a2 2 0 0 1-1.59-3.22L8.92 1.56Z" />
      </svg>
    </div>
  </Transition>
</template>
